import { Component, OnInit, TemplateRef, ViewChild, ChangeDetectionStrategy } from "@angular/core";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { BaseComponent, UserService, EventsService, HelperService } from "@mypxplat/xplat/core";
import { AppService, SeoService } from "@mypxplat/xplat/web/core";
import { ActivatedRoute } from "@angular/router";
import { BreadcrumbItem } from "../fender-ui/layout-header/layout-header.component";
import { TranslateService } from "@ngx-translate/core";

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "fui-event-detail",
  templateUrl: "fui-event-detail.component.html",
  standalone: false,
})
export class FuiEventDetailComponent extends BaseComponent implements OnInit {
  public event: any = null;
  public loading: boolean = true;
  public breadcrumbs: BreadcrumbItem[] = [];
  public formattedDate: string = "";
  public formattedEndTime: string = "";
  public locationString: string = "";
  public videoUrl: SafeResourceUrl | null = null;
  public htmlDescription: string = "";
  public userTimezone: string = Intl.DateTimeFormat().resolvedOptions().timeZone;
  public eventEnded: boolean = false;
  public isUpcoming: boolean = false;
  public isRecurring: boolean = false;
  public requiresUpgrade: boolean = false;

  // Reminder ("Remind Me") state
  public reminderSet: boolean = false;
  public reminderOffsets: number[] = [];
  public showReminderOptions: boolean = false;
  public reminderSaving: boolean = false;
  public reminderOptions: Array<{ value: number; label: string; checked: boolean }> = [
    { value: 1440, label: "1 day before", checked: false },
    { value: 60, label: "1 hour before", checked: false },
  ];

  @ViewChild("reminderModal") reminderModal: TemplateRef<any>;
  private reminderModalRef: any;

  constructor(
    userService: UserService,
    public appService: AppService,
    public eventsService: EventsService,
    private route: ActivatedRoute,
    private sanitizer: DomSanitizer,
    public helperService: HelperService,
    public translate: TranslateService,
    private _seo: SeoService
  ) {
    super(userService);
  }

  ngOnInit() {
    this.breadcrumbs = [{ label: "header.nav.home", routerLink: ["/home"] }, { label: "events.events", routerLink: ["/events"] }, { label: "" }];
    const idOrSlug = this.route.snapshot.params["id"];
    if (idOrSlug) {
      this.loadEvent(idOrSlug);
    }
  }

  loadEvent(idOrSlug: string) {
    this.loading = true;
    const cached = this.findCachedEvent(idOrSlug);
    if (cached) {
      this.setEvent(cached);
      this.loading = false;
    } else {
      this.eventsService.getPublicEvents().subscribe({
        next: () => {
          const found = this.findCachedEvent(idOrSlug);
          if (found) {
            this.setEvent(found);
          }
          this.loading = false;
        },
        error: () => {
          this.loading = false;
        },
      });
    }
  }

  private findCachedEvent(idOrSlug: string): any {
    const allEvents = this.eventsService.cachedAllEvents;
    if (!allEvents.length) return null;

    const match = allEvents.find((e) => e.id === idOrSlug || e.slug === idOrSlug);
    if (!match) return null;

    if (match.parent_event_id) {
      const siblings = allEvents.filter((e) => e.parent_event_id === match.parent_event_id);
      const upcoming = this.findNextUpcomingInstance(siblings);
      if (upcoming) return upcoming;
    }

    return match;
  }

  private findNextUpcomingInstance(instances: any[]): any {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const upcoming = instances
      .filter((e) => {
        const tz = e.timezone || "America/Chicago";
        const endDt = e.end_datetime || e.start_datetime;
        const endTime = this.parseDatetimeInTimezone(endDt, tz);
        return endTime >= startOfToday;
      })
      .sort((a, b) => (a.start_datetime || "").localeCompare(b.start_datetime || ""));
    return upcoming.length > 0 ? upcoming[0] : null;
  }

  private setEvent(event: any) {
    this.event = event;
    const rawHtml = this.helperService.containsHtml(event.description) ? event.description : this.helperService.makeHtml(event.description);
    this.htmlDescription = this.helperService.sanitizeQuillHtml(rawHtml);
    this.breadcrumbs = [{ label: "header.nav.home", routerLink: ["/home"] }, { label: "events.events", routerLink: ["/events"] }, { label: event.title }];
    this.formatDates();
    this.locationString = [event.address, event.city, event.state, event.country].filter(Boolean).join(", ");
    this.checkEventTiming();
    this.isRecurring = !!(event.parent_event_id || event.recurring_rule);
    this.requiresUpgrade = event.fsp_exclusive && !this.user?.active_subscription;
    const platform = this.eventsService.getLivestreamPlatform(event);
    const videoId = this.eventsService.getLivestreamVideoId(event);
    if (platform && videoId) {
      // Build the iframe URL for the appropriate host. Vimeo uses /video/{id}; YouTube uses
      // /embed/{id}. Both accept just the id — YouTube live works from the same watch id used
      // by a VOD, so no extra "is-live" flag is needed at this layer.
      const url = platform === "youtube" ? `https://www.youtube.com/embed/${videoId}` : `https://player.vimeo.com/video/${videoId}`;
      this.videoUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
    }
    if (this.user && this.isUpcoming) {
      this.loadReminderState();
    }
    this._applySeo();
  }

  private eventRef(): string {
    return this.event?.id || this.event?.slug;
  }

  /** Hydrates the current user's reminder subscription for this series (keyed by slug on the backend). */
  loadReminderState() {
    this.eventsService.getEventReminder(this.eventRef()).subscribe({
      next: (res) => {
        this.reminderSet = res.subscribed;
        this.reminderOffsets = res.offsets || [];
      },
      error: () => {},
    });
  }

  /** Opens the offset picker (or prompts anonymous users to sign in). */
  remindMe() {
    if (!this.user) {
      this.appService.redirectToConnect(true, false);
      return;
    }
    // Default: pre-check the user's existing selection, else pre-check both offsets.
    this.reminderOptions.forEach((o) => {
      o.checked = this.reminderOffsets.length ? this.reminderOffsets.includes(o.value) : true;
    });
    this.reminderModalRef = this.appService.showModal(this.reminderModal, { size: "md" });
  }

  toggleReminderOption(option: { value: number; checked: boolean }) {
    option.checked = !option.checked;
  }

  cancelReminderEdit() {
    this.reminderModalRef?.dismiss();
  }

  saveReminder() {
    const offsets = this.reminderOptions.filter((o) => o.checked).map((o) => o.value);
    if (!offsets.length) {
      this.appService.alert("Please select at least one reminder time.");
      return;
    }
    this.reminderSaving = true;
    this.eventsService.setEventReminder(this.eventRef(), offsets).subscribe({
      next: (res) => {
        this.reminderSet = true;
        this.reminderOffsets = res.offsets || offsets;
        this.reminderModalRef?.close();
        this.reminderSaving = false;
      },
      error: () => {
        this.reminderSaving = false;
        this.appService.alert("Sorry, we couldn't set your reminder. Please try again.");
      },
    });
  }

  cancelReminder() {
    this.reminderSaving = true;
    this.eventsService.deleteEventReminder(this.eventRef()).subscribe({
      next: () => {
        this.reminderSet = false;
        this.reminderOffsets = [];
        this.reminderModalRef?.close();
        this.reminderSaving = false;
      },
      error: () => {
        this.reminderSaving = false;
        this.appService.alert("Sorry, we couldn't cancel your reminder. Please try again.");
      },
    });
  }

  /** Downloads the .ics (Add to Calendar) file for this occurrence. */
  addToCalendar() {
    this.eventsService.downloadEventIcs(this.eventRef()).subscribe({
      next: (blob: Blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${this.event?.slug || "event"}.ics`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      },
      error: () => {
        this.appService.alert("Sorry, we couldn't generate the calendar file.");
      },
    });
  }

  private _applySeo() {
    if (!this.event) return;
    const description = (this.event.description || "").replace(/<[^>]*>/g, "");
    const trimmed = description ? description.substring(0, 200).trim() : "An event on MyFender.";
    const canonical = `https://my.fender.com/events/${this.event.slug || this.event.id}`;
    const isLivestream = this.eventsService.isLivestreamEvent(this.event);
    const jsonLd: any = {
      "@context": "https://schema.org",
      "@type": isLivestream ? "BroadcastEvent" : "Event",
      name: this.event.title,
      description: trimmed,
      url: canonical,
      startDate: this.event.start_datetime,
    };
    if (this.event.end_datetime) jsonLd.endDate = this.event.end_datetime;
    if (this.event.image_url) jsonLd.image = this.event.image_url;
    if (this.locationString) {
      jsonLd.location = {
        "@type": "Place",
        name: this.event.venue || this.locationString,
        address: this.locationString,
      };
    } else if (isLivestream) {
      jsonLd.eventAttendanceMode = "https://schema.org/OnlineEventAttendanceMode";
      jsonLd.location = { "@type": "VirtualLocation", url: canonical };
    }
    this._seo.setPageMeta({
      title: `${this.event.title} | MyFender Events`,
      description: trimmed,
      canonical,
      ogImage: this.event.image_url,
      ogType: "article",
      jsonLd,
    });
  }

  upgrade() {
    this.appService.goToCheckout("/onboarding?subscribeOnly=true");
  }

  private checkEventTiming() {
    if (!this.event?.start_datetime) return;
    const tz = this.event.timezone || "America/Chicago";
    const startTime = this.parseDatetimeInTimezone(this.event.start_datetime, tz).getTime();
    const endTime = this.event.end_datetime ? this.parseDatetimeInTimezone(this.event.end_datetime, tz).getTime() : startTime + 3600000;
    const now = Date.now();
    this.eventEnded = now > endTime;
    // "Remind Me" is only offered before an event starts (hidden once live or past).
    this.isUpcoming = now < startTime;
  }

  private parseDatetimeInTimezone(dt: string, timezone: string): Date {
    const parts = dt.split("T");
    const [y, mo, d] = parts[0].split("-").map(Number);
    const [h, mi] = (parts[1] || "00:00").split(":").map(Number);
    const fake = new Date(y!, mo! - 1, d!, h!, mi!);
    const inTz = new Date(fake.toLocaleString("en-US", { timeZone: timezone }));
    const offset = fake.getTime() - inTz.getTime();
    return new Date(fake.getTime() + offset);
  }

  private formatDates() {
    if (!this.event?.start_datetime) return;
    const tz = this.event.timezone || "America/Chicago";
    const start = this.parseDatetimeInTimezone(this.event.start_datetime, tz);
    this.formattedDate = start.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
    const startTime = start.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });

    if (this.event.end_datetime) {
      const end = this.parseDatetimeInTimezone(this.event.end_datetime, tz);
      const endTime = end.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
      this.formattedEndTime = `${startTime} - ${endTime}`;
    } else {
      this.formattedEndTime = startTime;
    }
  }
}
